import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'

import '../i18n/index' // Initialize i18n
import { UnifiedLandingPage } from './UnifiedLandingPage'
import { ContactPage } from './ContactPage'
import { AboutPage } from './AboutPage'
import { PrivacyPolicyPage } from './PrivacyPolicyPage'
import { HelpCenterPage } from './HelpCenterPage'
import { ImpressumPage } from './ImpressumPage'
import { TeamPage } from './TeamPage'
import { FAQPage } from './FAQPage'
import { ReportIssuePage } from './ReportIssuePage'
import { CareersPage } from './CareersPage'
import { PartnersPage } from './PartnersPage'
import { PartnershipsPage } from './PartnershipsPage' 
import { BookingPage } from './BookingPage'
import { StatusPage } from './StatusPage'
import { TermsOfServicePage } from './TermsOfServicePage'
import { CookiePolicyPage } from './CookiePolicyPage'
import { DocumentationPage } from './DocumentationPage'
import { ApiReferencePage } from './ApiReferencePage'
import { JobMeetApiDocs } from './JobMeetApiDocs'
import { ProductsPage } from './ProductsPage'
import { ProductDetailPage } from './ProductDetailPage'
import { CookieConsent } from './CookieConsent'
import { LegacyRouteHandler } from './RouteGuard'
import {
  ResponsiveDemoWithSuspense,
  SitemapXMLWithSuspense,
  SitemapViewerWithSuspense,
} from './LazyComponents'
import {
  HRTemplatesService,
  ComplianceRecallService,
  RareEarthPermitService,
  RaidToRevenueService,
  FiveGRecommerceService,
  VerpackGLoschungService,
} from './ServiceComponents'
import { SkipLinks } from './accessibility/SkipLinks'
import { LiveRegion } from './accessibility/LiveRegion'
import { ErrorBoundary, OfflineFallback, NoScriptFallback } from './errors'
import { useAccessibility } from '../hooks/useAccessibility'
import { PageCache, useMemoryCleanup } from './PageCache'
import { GlobalNavbar } from './GlobalNavbar'
import { DeveloperConsole } from './DeveloperConsole'
import { applyBrowserOptimizations, logBrowserInfo } from '../utils/browserOptimization'

// Apple-synchronized scroll handler - perfectly timed with transitions
const ScrollHandler: React.FC = () => {
  const location = useLocation()

  useEffect(() => {
    if (location.hash) {
      // Handle hash navigation (like #services)
      const element = document.getElementById(location.hash.substring(1))
      if (element) {
        // Wait for page transition to complete (350ms) + buffer
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' })
        }, 400)
      }
    } else {
      // Apple-style scroll synchronization - hidden during transition overlay
      const scrollToTop = () => {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
        window.scrollTo(0, 0)
      }

      // Wait for transition overlay to be fully opaque before any scrolling (150ms delay)
      setTimeout(() => {
        // Phase 1: During full overlay coverage (150-200ms) - completely hidden
        scrollToTop()
        requestAnimationFrame(scrollToTop)
        
        // Phase 2: Multiple scroll resets while overlay is solid (200-250ms)
        setTimeout(scrollToTop, 50)
        setTimeout(scrollToTop, 100)
        
        // Phase 3: Final scroll during overlay fade (250ms)
        setTimeout(() => {
          scrollToTop()
          
          // Add scroll lock during content fade-in
          const preventScroll = (e: Event) => {
            e.preventDefault()
            scrollToTop()
          }

          // Lock scroll during content animation (250-400ms) - optimized for performance
          window.addEventListener('scroll', preventScroll, { passive: false })
          window.addEventListener('wheel', preventScroll, { passive: false })
          window.addEventListener('touchmove', preventScroll, { passive: false })

          // Release scroll lock after transition completes
          setTimeout(() => {
            window.removeEventListener('scroll', preventScroll)
            window.removeEventListener('wheel', preventScroll)
            window.removeEventListener('touchmove', preventScroll)
            
            // Final scroll ensure after all animations complete
            scrollToTop()
          }, 150) // 400ms total - extended to match longer transition
        }, 100)
      }, 150) // Wait for overlay to be fully opaque
    }
  }, [location.pathname, location.hash])

  return null
}

// Page persistence eliminates the need for individual page transitions
// All transition logic is now handled by the PageCache component

// Apple-style auto-hiding scrollbar
const ScrollbarAutoHide: React.FC = () => {
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout

    const showScrollbar = () => {
      document.body.classList.add('show-scrollbar')
      clearTimeout(scrollTimeout)
      
      // Hide scrollbar after 800ms of inactivity (Apple-style)
      scrollTimeout = setTimeout(() => {
        document.body.classList.remove('show-scrollbar')
      }, 800)
    }

    // Show scrollbar on scroll
    window.addEventListener('scroll', showScrollbar, { passive: true })
    
    // Also show on mouse move near edge (for better UX)
    const handleMouseMove = (e: MouseEvent) => {
      if (window.innerWidth - e.clientX < 20) {
        showScrollbar()
      }
    }
    window.addEventListener('mousemove', handleMouseMove, { passive: true })

    return () => {
      window.removeEventListener('scroll', showScrollbar)
      window.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(scrollTimeout)
    }
  }, [])

  return null
}

// Apple-style link preloading for instant transitions
const LinkPreloader: React.FC = () => {
  useEffect(() => {
    const preloadLink = (href: string) => {
      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.href = href
      document.head.appendChild(link)
    }

    const handleLinkHover = (e: Event) => {
      const target = e.target as HTMLAnchorElement
      if (target.tagName === 'A' && target.href && target.href.startsWith(window.location.origin)) {
        preloadLink(target.href)
      }
    }

    // Add hover preloading to all internal links
    document.addEventListener('mouseover', handleLinkHover, { passive: true })
    
    return () => {
      document.removeEventListener('mouseover', handleLinkHover)
    }
  }, [])

  return null
}

// Apple-style page persistence with smooth transitions
const AnimatedRoutes: React.FC = () => {
  const location = useLocation()
  
  return (
    <LegacyRouteHandler>
      <ScrollbarAutoHide />
      <LinkPreloader />
      
      {/* Global Navbar - floats above all pages */}
      <GlobalNavbar />
      
      {/* Page Cache - keeps all visited pages mounted for instant navigation */}
      <PageCache maxCacheSize={15}>
        <Routes location={location} key={location.pathname}>
          {/* Main landing page */}
          <Route path="/" element={<UnifiedLandingPage />} />

          {/* About page */}
          <Route path="/about" element={<AboutPage />} />

          {/* Partners page */}
          <Route path="/partners" element={<PartnersPage />} />
          
          {/* Partnerships page */}
          <Route path="/partnerships" element={<PartnershipsPage />} />

          {/* Privacy Policy page */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* Terms of Service page */}
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Cookie Policy page */}
          <Route path="/cookies" element={<CookiePolicyPage />} />

          {/* Help Center page */}
          <Route path="/help" element={<HelpCenterPage />} />

          {/* Documentation page */}
          <Route path="/docs" element={<DocumentationPage />} />
          <Route path="/documentation" element={<DocumentationPage />} />
          
          {/* API Reference page */}
          <Route path="/developer/api" element={<ApiReferencePage />} />
          <Route path="/developer" element={<ApiReferencePage />} />
          
          {/* JobMeet API Documentation */}
          <Route path="/api/jobmeet" element={<JobMeetApiDocs />} />

          {/* Impressum page */}
          <Route path="/impressum" element={<ImpressumPage />} />

          {/* Team page */}
          <Route path="/team" element={<TeamPage />} />

          {/* FAQ page */}
          <Route path="/faq" element={<FAQPage />} />

          {/* Report Issue page */}
          <Route path="/report-issue" element={<ReportIssuePage />} />

          {/* Status page */}
          <Route path="/status" element={<StatusPage />} />

          {/* Contact page */}
          <Route path="/contact" element={<ContactPage />} />

          {/* Careers page */}
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/jobs" element={<CareersPage />} />

          {/* Booking page */}
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/book" element={<BookingPage />} />

          {/* Products page */}
          <Route path="/products" element={<ProductsPage />} />
          
          {/* JobMeet Product Detail Page */}
          <Route 
            path="/products/jobmeet" 
            element={
              <ProductDetailPage 
                id="jobmeet"
                name="JobMeet"
                icon="/Mernaki_Logo_Pack/transparent/mernaki-256.png"
                tagline="The premium meeting scheduling platform for modern professionals."
                description="JobMeet streamlines your scheduling process with automated booking, smart forms, and seamless calendar integration."
                features={[
                  "Smart Forms with AI logic",
                  "Seamless Calendar Sync (Google/iCloud)",
                  "Automated Email Confirmations",
                  "Premium Booking Interface"
                ]}
                externalLink="https://meet.mernaki.de"
                screenshots={[
                  "/jobmeet-images/Login.png",
                  "/jobmeet-images/Dashboard.png",
                  "/jobmeet-images/Calender.png",
                  "/jobmeet-images/Contacts.png",
                  "/jobmeet-images/Availability.png",
                  "/jobmeet-images/Branding.png"
                ]}
                orientation="landscape"
              />
            } 
          />

          {/* Division routes - scroll to division sections */}
          <Route path="/software" element={<UnifiedLandingPage />} />
          <Route path="/hardware" element={<UnifiedLandingPage />} />
          <Route path="/services" element={<UnifiedLandingPage />} />

          {/* Product-specific routes - scroll to division and highlight product */}
          <Route path="/codered" element={<UnifiedLandingPage />} />
          <Route path="/beyondreal" element={<UnifiedLandingPage />} />
          <Route path="/macx" element={<UnifiedLandingPage />} />
          <Route path="/macx-handheld" element={<UnifiedLandingPage />} />
          <Route path="/melanin-devices" element={<UnifiedLandingPage />} />

          {/* Demo page for responsive design showcase */}
          <Route path="/demo" element={<ResponsiveDemoWithSuspense />} />

          {/* SEO and sitemap routes */}
          <Route path="/sitemap.xml" element={<SitemapXMLWithSuspense />} />
          <Route path="/sitemap" element={<SitemapViewerWithSuspense />} />

          {/* Service routes with individual service applications */}
          {/* HR Templates Service - /hr-templates/* */}
          <Route path="/hr-templates" element={<HRTemplatesService />} />
          <Route path="/hr-templates/*" element={<HRTemplatesService />} />

          {/* Compliance Recall Insurance Service - /compliance-recall/* */}
          <Route path="/compliance-recall" element={<ComplianceRecallService />} />
          <Route path="/compliance-recall/*" element={<ComplianceRecallService />} />

          {/* Rare Earth Permit Service - /rare-earth-permit/* */}
          <Route path="/rare-earth-permit" element={<RareEarthPermitService />} />
          <Route path="/rare-earth-permit/*" element={<RareEarthPermitService />} />

          {/* Raid to Revenue Service - /raid-to-revenue/* */}
          <Route path="/raid-to-revenue" element={<RaidToRevenueService />} />
          <Route path="/raid-to-revenue/*" element={<RaidToRevenueService />} />

          {/* 5G Recommerce Service - /5g-recommerce/* */}
          <Route path="/5g-recommerce" element={<FiveGRecommerceService />} />
          <Route path="/5g-recommerce/*" element={<FiveGRecommerceService />} />

          {/* VerpackG Löschung Service - /verpackg-loschung/* */}
          <Route path="/verpackg-loschung" element={<VerpackGLoschungService />} />
          <Route path="/verpackg-loschung/*" element={<VerpackGLoschungService />} />

          {/* Legacy route redirects for backward compatibility */}
          <Route path="/templates" element={<Navigate to="/hr-templates" replace />} />
          <Route path="/templates/*" element={<Navigate to="/hr-templates" replace />} />
          <Route path="/hiring" element={<Navigate to="/hr-templates" replace />} />
          <Route path="/hiring/*" element={<Navigate to="/hr-templates" replace />} />
          <Route path="/hr" element={<Navigate to="/hr-templates" replace />} />
          <Route path="/hr/*" element={<Navigate to="/hr-templates" replace />} />
          <Route path="/compliance" element={<Navigate to="/compliance-recall" replace />} />
          <Route
            path="/compliance/*"
            element={<Navigate to="/compliance-recall" replace />}
          />
          <Route path="/trade" element={<Navigate to="/raid-to-revenue" replace />} />
          <Route path="/trade/*" element={<Navigate to="/raid-to-revenue" replace />} />
          <Route path="/intelligence" element={<Navigate to="/5g-recommerce" replace />} />
          <Route path="/intelligence/*" element={<Navigate to="/5g-recommerce" replace />} />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PageCache>
    </LegacyRouteHandler>
  )
}

export const App: React.FC = () => {
  const { announcements } = useAccessibility()
  
  // Enable memory cleanup for optimal performance
  useMemoryCleanup()

  // Disable browser's automatic scroll restoration and add global scroll behavior
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
    
    // Apply browser-specific optimizations
    applyBrowserOptimizations()
    
    // Log browser info for debugging
    if (process.env.NODE_ENV === 'development') {
      logBrowserInfo()
    }

    // Add global CSS for cross-browser optimized transitions
    const style = document.createElement('style')
    style.textContent = `
      html {
        scroll-behavior: auto !important;
      }
      
      /* Prevent any unwanted scroll restoration and layout shifts */
      html, body {
        overflow-x: hidden;
        background-color: #F7F5F5;
      }
      
      /* Ensure smooth page transitions */
      #root {
        position: relative;
        min-height: 100vh;
        background-color: #F7F5F5;
      }
      
      /* Prevent flash of unstyled content */
      body {
        opacity: 1;
        transition: opacity 0.1s ease-in-out;
      }
      
      /* Apple-style link preloading hint */
      a[href] {
        cursor: pointer;
      }
      
      /* Smooth transitions for all elements - Cross-browser */
      * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      
      /* Prevent flickering during fast scrolling - Cross-browser */
      .scroll-optimized {
        will-change: transform;
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }
      
      /* Optimize scroll performance */
      body {
        -webkit-overflow-scrolling: touch;
        scroll-behavior: auto;
      }
      
      /* Page cache optimizations - Reduced contain for Chrome/Brave */
      .page-cache-container {
        contain: layout style;
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        -webkit-perspective: 1000px;
        perspective: 1000px;
      }
      
      /* Optimize cached page rendering - Cross-browser */
      .page-cache-container > div {
        contain: layout style;
        will-change: auto;
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }
      
      /* Active page gets will-change */
      .page-cache-container > div[style*="pointer-events: auto"] {
        will-change: transform, opacity;
      }
      
      /* Prevent layout shifts during transitions */
      [data-scroll-locked] {
        overflow: hidden !important;
        position: fixed !important;
        width: 100% !important;
        height: 100% !important;
      }
      
      /* Smooth font rendering for all cached pages */
      .page-cache-container * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
      }
      
      /* Chrome/Brave specific fixes */
      @media screen and (-webkit-min-device-pixel-ratio: 0) {
        .page-cache-container,
        .page-cache-container > div {
          -webkit-transform: translate3d(0, 0, 0);
          transform: translate3d(0, 0, 0);
        }
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  return (
    <ErrorBoundary>
      <NoScriptFallback />
      <OfflineFallback>
        <HelmetProvider>
          <Router future={{ v7_startTransition: true }}>
            <SkipLinks />
            <LiveRegion messages={announcements} />
            <ScrollHandler />
            <AnimatedRoutes />
            
            {/* Enhanced Developer Console - Fixed position, always on top */}
            <DeveloperConsole />

            <CookieConsent />
          </Router>
        </HelmetProvider>
      </OfflineFallback>
    </ErrorBoundary>
  )
}
